/**
 * Writes, each followed by an AWAITED cache invalidation of the threads and
 * counts for the building. Awaited on purpose: a caller that hands the result
 * straight to a picker or a card must see the refetched rows, not the stale
 * cache (the Radix-Select lesson in what-if-app/CLAUDE.md).
 *
 * An issue-backed thread's replies are its sub-issue's comments, so those
 * writes go to the PM tables (`CommentTarget.issueId`); the Issues page and
 * this surface then read the same rows.
 */

import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { documentCommentKeys } from "./queries";
import {
  addReply,
  createThread,
  deleteComment,
  deleteThread,
  editComment,
  markMentionsRead,
  promoteThreadToIssue,
  setThreadResolved,
  type CommentTarget,
  type CreateThreadResult,
} from "./repository";
import type { CommentSource, DocumentCommentScope } from "./types";

export interface DocumentCommentMutations {
  createThread: (input: {
    pageNumber: number | null;
    body: string;
    mentionedUserIds: number[];
    createIssue: boolean;
  }) => Promise<CreateThreadResult>;
  reply: (input: {
    target: Omit<CommentTarget, "authorId">;
    body: string;
    mentionedUserIds: number[];
  }) => Promise<number>;
  edit: (input: {
    commentId: number;
    source: CommentSource;
    body: string;
    mentionedUserIds: number[];
  }) => Promise<void>;
  remove: (input: { commentId: number; source: CommentSource }) => Promise<void>;
  removeThread: (threadId: number) => Promise<void>;
  resolve: (thread: { id: number; issueId: number | null }) => Promise<void>;
  reopen: (thread: { id: number; issueId: number | null }) => Promise<void>;
  /** File an opted-out thread as a sub-issue; resolves to the issue id. */
  promote: (threadId: number) => Promise<number>;
  markRead: (items: Array<{ commentId: number; source: CommentSource }>) => Promise<void>;
  /** Any write in flight — cards disable their actions on it. */
  isPending: boolean;
}

export function useDocumentCommentMutations(
  scope: DocumentCommentScope | null,
  currentUserId: number | null
): DocumentCommentMutations {
  const queryClient = useQueryClient();
  const buildingId = scope?.buildingId ?? null;

  const flush = useCallback(async () => {
    if (buildingId == null) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: documentCommentKeys.threadsForBuilding(buildingId),
      }),
      queryClient.invalidateQueries({
        queryKey: documentCommentKeys.countsForBuilding(buildingId),
      }),
      queryClient.invalidateQueries({ queryKey: ["document-comment-thread-activity"] }),
      queryClient.invalidateQueries({ queryKey: ["document-comment-issue-activity"] }),
    ]);
  }, [buildingId, queryClient]);

  const requireUser = (): number => {
    if (currentUserId == null) throw new Error("Not signed in");
    return currentUserId;
  };

  const create = useMutation({
    mutationFn: async (input: {
      pageNumber: number | null;
      body: string;
      mentionedUserIds: number[];
      createIssue: boolean;
    }) => {
      if (!scope) throw new Error("No document in scope");
      const result = await createThread({ documentId: scope.id, ...input });
      await flush();
      return result;
    },
  });

  const reply = useMutation({
    mutationFn: async (input: {
      target: Omit<CommentTarget, "authorId">;
      body: string;
      mentionedUserIds: number[];
    }) => {
      const id = await addReply(
        { ...input.target, authorId: requireUser() },
        input.body,
        input.mentionedUserIds
      );
      await flush();
      return id;
    },
  });

  const edit = useMutation({
    mutationFn: async (input: {
      commentId: number;
      source: CommentSource;
      body: string;
      mentionedUserIds: number[];
    }) => {
      await editComment(
        input.commentId,
        input.source,
        input.body,
        input.mentionedUserIds,
        requireUser()
      );
      await flush();
    },
  });

  const remove = useMutation({
    mutationFn: async (input: { commentId: number; source: CommentSource }) => {
      await deleteComment(input.commentId, input.source);
      await flush();
    },
  });

  const removeThread = useMutation({
    mutationFn: async (threadId: number) => {
      await deleteThread(threadId);
      await flush();
    },
  });

  const setResolved = useMutation({
    mutationFn: async (input: {
      thread: { id: number; issueId: number | null };
      resolved: boolean;
    }) => {
      await setThreadResolved(input.thread, input.resolved);
      await flush();
    },
  });

  const promote = useMutation({
    mutationFn: async (threadId: number) => {
      const issueId = await promoteThreadToIssue(threadId);
      await flush();
      return issueId;
    },
  });

  const read = useMutation({
    mutationFn: async (items: Array<{ commentId: number; source: CommentSource }>) => {
      await markMentionsRead(items, requireUser());
      await flush();
    },
  });

  const isPending =
    create.isPending ||
    reply.isPending ||
    edit.isPending ||
    remove.isPending ||
    removeThread.isPending ||
    setResolved.isPending ||
    promote.isPending;

  return {
    createThread: create.mutateAsync,
    reply: reply.mutateAsync,
    edit: edit.mutateAsync,
    remove: remove.mutateAsync,
    removeThread: removeThread.mutateAsync,
    resolve: (thread) => setResolved.mutateAsync({ thread, resolved: true }),
    reopen: (thread) => setResolved.mutateAsync({ thread, resolved: false }),
    promote: promote.mutateAsync,
    markRead: read.mutateAsync,
    isPending,
  };
}
